import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import SEO from "@/components/SEO";
import { KineticText } from "@/components/KineticText";
import Reveal from "@/components/Reveal";
import EditorialMarquee from "@/components/EditorialMarquee";
import { PRODUCTS, BUSINESS } from "@/lib/content";

const enquireHref = (name) =>
  `/contact?product=${encodeURIComponent(name)}`;

export default function Products() {
  return (
    <>
      <SEO
        title="Products"
        description="Fresh wholesale vegetables from Pravin Hiralal Prajapati: Capsicum, Cucumber (Kheera), Green Peas (Mutter), Cabbage and Carrot — quality-graded and available daily in bulk."
        path="products"
      />

      <section className="relative pt-40 pb-20 md:pt-52 md:pb-24 max-w-screen-2xl mx-auto px-6 md:px-12">
        <span className="font-mono text-xs uppercase tracking-[0.28em] text-brand-terra">Our Produce</span>
        <KineticText
          as="h1"
          lines={["Fresh. Premium.", "Daily."]}
          className="font-display text-5xl md:text-8xl text-brand-forest mt-6 leading-[0.92] tracking-tight"
        />
        <p className="mt-8 max-w-2xl text-brand-muted text-lg leading-relaxed">
          Every lot is hand-selected and graded for freshness, size and colour before it reaches your business.
          Enquire for bulk pricing on any of our vegetables.
        </p>
      </section>

      <EditorialMarquee />

      <section className="py-20 md:py-28 max-w-screen-2xl mx-auto px-6 md:px-12 space-y-16 md:space-y-28">
        {PRODUCTS.map((p, i) => (
          <div
            key={p.slug}
            data-testid={`product-${p.slug}`}
            className={`grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-center ${i % 2 === 1 ? "lg:[&>*:first-child]:order-2" : ""}`}
          >
            <Reveal className="lg:col-span-6">
              <div className="relative">
                <div className="absolute inset-6 bg-brand-leaf/10 rounded-full blur-3xl" />
                <div className="overflow-hidden rounded-t-[10rem] rounded-b-3xl aspect-[4/5] bg-brand-sand relative">
                  <img src={p.image} alt={p.name} className="w-full h-full object-cover hover:scale-105 transition-transform duration-700" />
                </div>
              </div>
            </Reveal>

            <Reveal delay={0.1} className="lg:col-span-6">
              <div className="flex items-baseline gap-4">
                <span className="font-mono text-brand-terra text-sm">0{i + 1}</span>
                <span className="font-display italic text-brand-leaf text-2xl">{p.nameGu}</span>
              </div>
              <h2 className="font-display text-4xl md:text-6xl text-brand-forest tracking-tight mt-3 leading-none">{p.name}</h2>
              <p className="mt-6 text-brand-muted text-lg leading-relaxed max-w-lg">{p.blurb}</p>
              <div className="flex flex-wrap gap-2 mt-6">
                {p.tags.map((t) => (
                  <span key={t} className="rounded-full bg-brand-sand text-brand-forest text-sm px-4 py-2 font-medium">{t}</span>
                ))}
              </div>
              <Link
                to={enquireHref(p.name)}
                data-testid={`enquire-${p.slug}`}
                className="inline-flex items-center gap-2 mt-8 rounded-full bg-brand-forest text-brand-bone px-7 py-4 font-medium hover:bg-brand-forest-light transition-colors duration-300 active:scale-95"
              >
                Enquire Bulk <ArrowUpRight size={18} />
              </Link>
            </Reveal>
          </div>
        ))}
      </section>

      <section className="pb-24 max-w-screen-2xl mx-auto px-6 md:px-12">
        <div className="rounded-[2.5rem] bg-brand-forest text-brand-bone p-10 md:p-16 text-center grain relative overflow-hidden">
          <h2 className="font-display text-3xl md:text-5xl tracking-tight relative z-10">Need a product not listed here?</h2>
          <p className="text-brand-bone/70 mt-4 relative z-10">Message us on WhatsApp — we source across the Karnavati network.</p>
          <a
            href={`https://wa.me/${BUSINESS.whatsapp}`}
            target="_blank"
            rel="noopener noreferrer"
            className="relative z-10 inline-flex items-center gap-2 mt-8 rounded-full bg-brand-leaf text-white px-8 py-4 font-medium hover:bg-brand-leaf-light transition-colors duration-300 active:scale-95"
          >
            Chat on WhatsApp <ArrowUpRight size={18} />
          </a>
        </div>
      </section>
    </>
  );
}
